import { useState, useEffect, useRef } from 'react';
import { getPortfolio } from '../../../lib/api';
import { Link } from 'react-router-dom';
import { stripHtml } from '../../../lib/utils';

interface PortfolioItem {
  id: string;
  title: string;
  slug: string;
  description: string;
  featured_image: string;
  category: string;
  status: string;
  created_at: string;
}

export default function Portfolio() {
  const [isVisible, setIsVisible] = useState(false);
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const data = await getPortfolio({ status: 'published' }) as PortfolioItem[];
      setPortfolioItems((data ?? []).slice(0, 6));
    } catch (error) {
      console.error('ポートフォリオの取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractTags = (description: string): string[] => {
    const tagMatch = description.match(/タグ[:：](.+)/);
    if (tagMatch) {
      return tagMatch[1].split(/[、,]/).map(tag => tag.trim()).filter(Boolean).slice(0, 3);
    }
    return [];
  };

  if (loading) {
    return (
      <section id="portfolio" ref={sectionRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">読み込み中...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio" ref={sectionRef} className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-1 bg-black"></div>
            <span className="text-sm font-medium tracking-widest uppercase text-gray-600">Portfolio</span>
            <div className="w-12 h-1 bg-black"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            制作実績
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            これまでに手がけた様々なプロジェクトをご紹介いたします
          </p>
        </div>

        {portfolioItems.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-folder-open-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-600">実績がまだ登録されていません</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {portfolioItems.map((item, index) => {
                const tags = extractTags(item.description);
                return (
                  <Link
                    key={item.id}
                    to={`/portfolio/${item.id}`}
                    className={`group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform cursor-pointer ${
                      isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={item.featured_image}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="p-6">
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                          {item.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors duration-300">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 mb-4 text-sm leading-relaxed line-clamp-3">
                        {stripHtml(item.description.replace(/タグ[:：].+/, '').trim())}
                      </p>
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {tags.map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-md font-medium whitespace-nowrap"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium whitespace-nowrap cursor-pointer"
              >
                <span>すべての実績を見る</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
